import React, { useState } from 'react';

import AllScoresOverview from './components/AllScoresOverview/AllScoresOverview';
import ModalPortal from '../ModalPortal/ModalPortal';
import SingleScorePager from './components/SingleScorePager/SingleScorePager';

import { MainDerailmentSectionOverview } from './components/MainOverview';
import { useSingleScaleModal } from './components/SingleScorePager/useModal';
import { TOXICS_HIGH_RISK_SCORE } from './constants';

import style from './style/index.scss';
import { filterScoresByRisks } from './utils';

/**
 * filters scores with high risk or very high risk assessment
 * and sorts them by score ascending
 * and returns the first 6 scores
 * if there are less than 6 scores with high risk or very high risk assessment
 * then the first 3 scores are returned
 *
 * @param {Object[]} scores - scores
 *
 * @returns {Object[]} - scores with risks
 */
export function getScoresWithRisksSorted(scores) {
    const risks = filterScoresByRisks(scores);

    if (risks.length < 6) {
        return risks.splice(0, 3);
    }

    return risks.splice(0, 6);
}

/**
 * @param {Object} props - props object
 * @param {Object} props.section - section object
 * @param {Object} props.i18nParticipation - i18n object
 * @param {Object} props.sectionIconClasses - icon classes
 *
 * @returns {JSX.Element} - main derailment section overview
 */
export function Toxics(props) {
    const { section, i18nParticipation, sectionIconClasses } = props;

    const [isAllScoresModalOpen, setIsAllScoresModalOpen] = useState(false);
    const singleScaleModal = useSingleScaleModal(section.scores);

    const hasHighRiskScore = section.scores.some(({ score }) => score >= TOXICS_HIGH_RISK_SCORE);
    const scoreWithRisks = getScoresWithRisksSorted(section.scores);

    const showAllScoresModal = () => {
        setIsAllScoresModalOpen(true);
    };
    const closeAllScoresModal = () => {
        setIsAllScoresModalOpen(false);
    };

    const openSingleScaleScoreModal = evt => {
        setIsAllScoresModalOpen(false);
        singleScaleModal.onOpen(evt);
    };

    return (
        <div className={style.content}>
            <MainDerailmentSectionOverview
                i18nParticipation={i18nParticipation}
                hasHighRiskScore={hasHighRiskScore}
                section={section}
                scoreWithRisks={scoreWithRisks}
                openSingleScaleScoreModal={openSingleScaleScoreModal}
                showAllScoresModal={showAllScoresModal}
            />

            <ModalPortal isOpen={singleScaleModal.isOpen} onClose={singleScaleModal.onClose}>
                <SingleScorePager
                    i18nParticipation={i18nParticipation}
                    title={section.title}
                    score={section.scores[singleScaleModal.visibleScaleIndex]}
                    onClose={singleScaleModal.onClose}
                    canGoBack={singleScaleModal.canGoBack}
                    goBack={singleScaleModal.goBack}
                    canGoNext={singleScaleModal.canGoNext}
                    goNext={singleScaleModal.goNext}
                    openAllScoresModal={() => {
                        singleScaleModal.onClose();
                        showAllScoresModal();
                    }}
                />
            </ModalPortal>

            <ModalPortal isOpen={isAllScoresModalOpen} onClose={closeAllScoresModal}>
                <AllScoresOverview
                    sectionIconClasses={sectionIconClasses}
                    i18nParticipation={i18nParticipation}
                    title={section.title}
                    basedOn={section.basedOn}
                    scores={section.scores}
                    onClose={closeAllScoresModal}
                    openSingleScaleScoreModal={openSingleScaleScoreModal}
                />
            </ModalPortal>
        </div>
    );
}
